import type {Component, Editor} from 'grapesjs';
import type {ElementData, ImageData, StickerData, VideoData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import type {ImageItemSourceType} from '@Libraries/image-item.library';
import {ImageItemSource} from '@Libraries/image-item.library';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import {GA4EventName} from '@Libraries/ga-events';
import {UserImageSource} from '@Libraries/user-image-library';
import {UserVideoSource} from '@Libraries/user-video-library';
import {StockSources} from '@Libraries/pmw-stock-media-library';
import {convertComponentIfNeeded} from '@Libraries/email-campaigns-library';
import {noop} from '@Utils/general.util';
import {openDesignSelectorAndInsert} from '@EmailMakerReact/lib/email-maker-design-library';
import {openMessageModal} from '@Components/modals/message-modal';
import {openErrorModal} from '@Modals/error-modal';
import {openMediaTypeModal} from '@Modals/media-type-modal/media-type-modal';
import {MediaType} from '@Panels/media-type-panel/media-type-panel';
import type {StockModalProps} from '@Modals/stock-modal';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import type {AiPanelKey} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button.types';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {EmbedVideosEmailmakerInputModalProps} from '@Modals/embed-videos-emailmaker-input-modal';
import {openEmbedVideosEmailmakerModal} from '@Modals/embed-videos-emailmaker-input-modal';
import {EMAIL_MAKER_BLOCKS, EMAIL_MAKER_COMMANDS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, MAX_VIDEOS_ALLOWED} from '@EmailMakerReact/email-maker-react.constants';
import {appendBlockOnClick} from '@EmailMakerReact/lib/email-maker-block-insert-library';
import {EmailMakerMediaType} from '@EmailMakerReact/email-maker-react.types';
import {DATA_URL_ATTRIBUTE, EMBEDDED_VIDEO_ATTRIBUTE, removeComponentAnchor} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {getVideoCount, setEmbeddedVideoSrc, setImageLoading, setImageSrc} from '@EmailMakerReact/lib/email-maker-media-library';
import {trackGaEventForComponent} from '@EmailMakerReact/lib/email-maker-misc-library';
import {saveEmailTemplateToCache} from '@EmailMakerReact/lib/email-maker-storage-library';
import type {EmailStockPopupRequest} from '@EmailMakerReact/components/email-stock-media-popup/email-stock-media-popup.types';
import {openEmailStockMediaPopup} from '@EmailMakerReact/components/email-stock-media-popup/email-stock-popup-controller';
import {setSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import {IMAGE_BLOCK_ANCHOR_ID} from '@Components/email-maker-block-manager/email-maker-block-manager.types';

/**
 * Media pickers for the React email maker. On the desktop viewport the stock pickers open as a floating popup
 * anchored to the left panel (mirroring the poster editor); smaller viewports keep the full-screen modal.
 * Selected media is applied through the media library, which owns the setImageSrc /
 * setEmbeddedVideoSrc pipeline and the endpoint contracts.
 */

/** Per-flow stock panel ids — the stock panel keys its Redux state (selected tab, grid) by panelId. */
const STICKER_STOCK_PANEL_ID = 'STOCK_MEDIA_VIA_EMAIL_EDITOR_STICKER';
const ICONS_STOCK_PANEL_ID = 'STOCK_MEDIA_VIA_EMAIL_EDITOR_ICON';
const IMAGE_STOCK_PANEL_ID = 'STOCK_MEDIA_VIA_EMAIL_EDITOR_IMAGE';
const AI_IMAGES_STOCK_PANEL_ID = 'STOCK_MEDIA_VIA_EMAIL_EDITOR_AI';
const VIDEO_STOCK_PANEL_ID = 'STOCK_MEDIA_VIA_EMAIL_EDITOR_VIDEO';

/** Single tab → the stock panel opens straight in grid view, matching the desktop chooser's Photos grid. */
export const EMAIL_EXPLORE_MEDIA_STOCK_TABS: StockPanelTabs[] = [StockPanelTabs.PHOTOS];

const TOUCH_MODAL_OPEN_DELAY_MS = 50;

/**
 * A canvas double-tap's synthesized dblclick opens these modals from inside the second tap's touchend. The browser
 * then fires the tap's compatibility click at the touch point — hitting the freshly-mounted modal overlay and
 * dismissing it. Deferring the mount lets that click land on the canvas instead.
 */
export function openModalAfterTouchSettles(openModal: () => void): void {
  setTimeout(openModal, TOUCH_MODAL_OPEN_DELAY_MS);
}

/** Mirrors resolveEditorViewport's DESKTOP threshold — the popup anchors to the left panel, which only renders there. */
export function isDesktopEmailEditorViewport(): boolean {
  return window.innerWidth > parseInt(ScreenBreakpoints.LARGE, 10);
}

function openStockMediaPicker(pickerProps: StockModalProps, initialView?: EmailStockPopupRequest['initialView']): void {
  if (isDesktopEmailEditorViewport()) {
    openEmailStockMediaPopup({overrides: pickerProps, initialView, onNavigateBack: getOpenBlockPanelBackTarget(), arrowAnchorTileId: getArrowAnchorTileIdForOpenBlockPanel()});
    return;
  }
  openModalAfterTouchSettles((): void => {
    openStockMediaModal({...pickerProps, initialAiPanel: getModalAiPanelForInitialView(initialView)});
  });
}

/** The AI popup views map to the stock panel's self-hosted AI panels, so phone gets the same AI panels as the poster editor. */
function getModalAiPanelForInitialView(initialView?: EmailStockPopupRequest['initialView']): AiPanelKey | undefined {
  if (initialView === 'ai-images' || initialView === 'ai-stickers' || initialView === 'ai-videos') {
    return initialView;
  }
  return undefined;
}

/** Sticker targets get the AI generator preset to stickers; other image-family targets (images, icons) generate photos. */
export function getAiImagesPanelKeyForComponent(component: Component): 'ai-images' | 'ai-stickers' {
  return component.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER) ? 'ai-stickers' : 'ai-images';
}

/**
 * A block panel still open at this point means the popup was launched from one of its tiles (the tile click
 * handler closes the panel only after the block's activate has run) — Back should reopen that panel.
 */
function getOpenBlockPanelBackTarget(): (() => void) | undefined {
  const openBlockPanel = window.PMW.redux.store.getState().emailMakerBlockManager.selectedBlockPanel;
  if (!openBlockPanel) {
    return undefined;
  }
  return (): void => {
    window.PMW.redux.store.dispatch(setSelectedBlockPanel(openBlockPanel));
  };
}

/**
 * The left-menu tile the popup arrow should point at: the Image tile when the flow was launched from the
 * Image block panel; undefined otherwise (the popup then defaults to the Elements tile).
 */
function getArrowAnchorTileIdForOpenBlockPanel(): string | undefined {
  const openBlockPanel = window.PMW.redux.store.getState().emailMakerBlockManager.selectedBlockPanel;
  if (openBlockPanel === window.PMW.emails.blocks.CATEGORY_IMAGE) {
    return IMAGE_BLOCK_ANCHOR_ID;
  }
  return undefined;
}

export interface OpenMediaPickerModalOptions<ItemType> {
  onAddSelected: (items: ItemType[]) => void;
  onStartAddSelected: () => void;
}

/**
 * Opens the picker for one media type when `onlyMediaType` is given. Otherwise, opens the media-type chooser, or the
 * image picker when the video limit is hit */
export function openMediaModal(editor: Editor, options: OpenMediaPickerModalOptions<ImageData>, onlyMediaType?: MediaType): void {
  const videosAllowedCount = MAX_VIDEOS_ALLOWED - getVideoCount(editor);
  const sharedModalProps = {
    onStartAddSelected: options.onStartAddSelected,
    onAddSelected: options.onAddSelected,
    onAddBrandAsset: (): void => {
      void onAddBrandAsset(editor);
    },
    maxSelectionNumber: 1,
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    videoQualityFiltersDisabled: true,
    hideRecordingOptions: true,
    exploreMediaStockTabs: isDesktopEmailEditorViewport() ? undefined : EMAIL_EXPLORE_MEDIA_STOCK_TABS,
  };

  if (onlyMediaType !== undefined) {
    openMediaTypeModal({mediaType: onlyMediaType, ...sharedModalProps});
    return;
  }

  if (videosAllowedCount <= 0) {
    openMessageModal({
      title: window.i18next.t('pmwjs_video_limit'),
      text: window.i18next.t('pmwjs_video_limit_message', {num: MAX_VIDEOS_ALLOWED}),
      onClose: (): void => {
        openMediaTypeModal({mediaType: MediaType.IMAGE, ...sharedModalProps});
      },
    });
    return;
  }

  openMediaTypeModal(sharedModalProps);
}

/** Stock sticker picker for the component (or selection). */
export function openStickerModal(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaPicker({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    tabsToShow: [StockPanelTabs.STICKERS],
    onAddSelected: (stickers: StickerData[]): void => {
      handleStockMediaStickerSelection(editor, stickers, target);
    },
    maxSelectionNumber: 1,
    defaultSearchTab: StockPanelTabs.STICKERS,
    hideOnClose: false,
    panelId: STICKER_STOCK_PANEL_ID,
    desktopHeaderTitle: window.i18next.t('pmwjs_stickers'),
  });
}

/** Stock icons picker for the component (or selection). */
export function openIconsModal(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaPicker({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    tabsToShow: [StockPanelTabs.ICONS],
    onAddSelected: (icons: ElementData[]): void => {
      handleStockMediaIconSelection(editor, icons, target);
    },
    maxSelectionNumber: 1,
    defaultSearchTab: StockPanelTabs.ICONS,
    hideOnClose: false,
    panelId: ICONS_STOCK_PANEL_ID,
    desktopHeaderTitle: window.i18next.t('pmwjs_icons'),
  });
}

/** Explore-media for the component (or selection), opening the grid matching the component's media type (sticker/icon/image). */
export function openExploreMediaForSelection(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  if (target.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER)) {
    openStickerModal(editor, target);
    return;
  }

  if (target.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON)) {
    openIconsModal(editor, target);
    return;
  }

  openExploreMediaImage(editor, target);
}

/** Persists the template (so the brand asset is referenced) before running the callback. */
export async function onAddBrandAsset(editor: Editor, cb?: VoidFunction): Promise<void> {
  await saveEmailTemplateToCache(editor);
  cb?.();
}

/** Image source picker for the component (or selection). Desktop opens the Image chooser popup. */
export function openAddImageOptionSelector(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  if (isDesktopEmailEditorViewport()) {
    openEmailStockMediaPopup({
      overrides: buildStockImagesPickerProps(editor, target),
      initialView: 'image-options',
      onNavigateBack: getOpenBlockPanelBackTarget(),
      arrowAnchorTileId: getArrowAnchorTileIdForOpenBlockPanel(),
      imageOptions: {
        onMyUploads: (): void => {
          openMyUploadsImage(editor, target);
        },
        onMyDesigns: (): void => {
          openDesignSelectorAndInsert(editor, {isMultiSelectAllowed: false, replaceComponent: true});
        },
      },
    });
    return;
  }

  openModalAfterTouchSettles((): void => {
    openMediaTypeModal({
      onStartAddSelected: createImageLoadingStarter(editor, target),
      onAddSelected: (images: ImageData[]): void => {
        handleStockMediaImageSelection(editor, images, target);
      },
      maxSelectionNumber: 1,
      mediaType: MediaType.IMAGE,
      title: window.i18next.t('pmwjs_select_an_option'),
      blacklistedSources: [StockSources.PMW_STOCK_IMAGE],
      hideRecordingOptions: true,
      uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
      exploreMediaStockTabs: EMAIL_EXPLORE_MEDIA_STOCK_TABS,
      aiImagesPanelKey: getAiImagesPanelKeyForComponent(target),
      onExploreMedia: getExploreMediaOverrideForComponent(editor, target),
    });
  });
}

/**
 * Sticker and icon components must land on their own stock grid (with the matching selection handler), not the
 * generic Photos grid the image chooser opens inline — mirrors the desktop chooser's openExploreMediaForSelection routing.
 */
function getExploreMediaOverrideForComponent(editor: Editor, component: Component): (() => void) | undefined {
  if (!component.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER) && !component.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON)) {
    return undefined;
  }
  return (): void => {
    openExploreMediaForSelection(editor, component);
  };
}

/** My-uploads image picker for the component (or selection). */
export function openMyUploadsImage(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openUserMediaModal({
    onStartAddSelected: createImageLoadingStarter(editor, target),
    onAddSelected: (images: ImageData[]): void => {
      handleStockMediaImageSelection(editor, images, target);
    },
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.IMAGE,
    mediaTypesToShow: [UploadsMediaType.IMAGE],
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    uploadAcceptedMimetype: 'image/*',
    hideFooterSecondaryActions: true,
  });
}

/** Stock photos picker for the component (or selection). */
export function openExploreMediaImage(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaPicker(buildStockImagesPickerProps(editor, target));
}

/** The Photos stock panel props, shared by the direct Explore-media flow and the Image chooser popup. */
function buildStockImagesPickerProps(editor: Editor, component: Component): StockModalProps {
  return {
    onStartAddSelected: createImageLoadingStarter(editor, component),
    onAddSelected: (images: ImageData[]): void => {
      handleStockMediaImageSelection(editor, images, component);
    },
    maxSelectionNumber: 1,
    tabsToShow: [StockPanelTabs.PHOTOS],
    defaultSearchTab: StockPanelTabs.PHOTOS,
    populateRecentlyAddedOnItemAdd: true,
    panelId: IMAGE_STOCK_PANEL_ID,
    desktopHeaderTitle: window.i18next.t('pmwjs_photos_2'),
  };
}

/** Text-to-image picker for the component (or selection). Desktop opens the AI image generator directly in the popup. */
export function openAiImages(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaPicker(
    {
      onStartAddSelected: createImageLoadingStarter(editor, target),
      onAddSelected: (images: ImageData[]): void => {
        handleStockMediaImageSelection(editor, images, target);
      },
      maxSelectionNumber: 1,
      tabsToShow: [StockPanelTabs.PHOTOS],
      defaultSearchTab: StockPanelTabs.PHOTOS,
      populateRecentlyAddedOnItemAdd: true,
      panelId: AI_IMAGES_STOCK_PANEL_ID,
    },
    getAiImagesPanelKeyForComponent(target)
  );
}

/** Guards against the chooser re-opening itself: the deferred placeholder append fires the video block's activate, which runs this selector. */
let isVideoPlaceholderAppendInProgress = false;

/**
 * Video source chooser for a fresh add (Elements panel → Video tile). Mirrors the image blocks' behavior:
 * no placeholder is dropped on tile click — it is only added once a chosen source actually needs it.
 */
export function openAddVideoBlockChooser(editor: Editor): void {
  const getOrCreatePlaceholder = createVideoPlaceholderProvider(editor);

  openEmailStockMediaPopup({
    overrides: buildStockVideosPickerPropsForTarget(editor, getOrCreatePlaceholder),
    initialView: 'video-options',
    exitOnSubViewBack: true,
    onNavigateBack: getOpenBlockPanelBackTarget(),
    arrowAnchorTileId: getArrowAnchorTileIdForOpenBlockPanel(),
    videoOptions: {
      onSourceSelected: (): void => {
        getOrCreatePlaceholder();
      },
      onMyUploads: (): void => {
        const placeholder = getOrCreatePlaceholder();
        if (placeholder) {
          openMyUploadsVideo(editor, placeholder);
        }
      },
      onEmbedVideo: (): void => {
        const placeholder = getOrCreatePlaceholder();
        if (placeholder) {
          openEmbedVideosEmailmakerModal(getEmbedVideoModalProps(editor, placeholder));
        }
      },
    },
  });
}

/** Memoized placeholder factory: appends the video block on first use, suppressing its activate-driven chooser. */
function createVideoPlaceholderProvider(editor: Editor): () => Component | undefined {
  let placeholder: Component | undefined;
  return (): Component | undefined => {
    if (placeholder) {
      return placeholder;
    }
    const videoBlock = editor.BlockManager.get(EMAIL_MAKER_BLOCKS.ID_VIDEO);
    if (!videoBlock) {
      return undefined;
    }
    isVideoPlaceholderAppendInProgress = true;
    try {
      placeholder = appendBlockOnClick(editor, videoBlock);
    } finally {
      isVideoPlaceholderAppendInProgress = false;
    }
    if (placeholder) {
      editor.runCommand(EMAIL_MAKER_COMMANDS.REFRESH_MEDIA_QUERIES, {component: placeholder, responsiveStyles: ['width']});
    }
    return placeholder;
  };
}

/** Video source picker (including embed-video entry) for the component (or selection). Desktop opens the Video chooser popup. */
export function openAddVideoOptionSelector(editor: Editor, component?: Component): void {
  if (isVideoPlaceholderAppendInProgress) {
    return;
  }

  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  const videoModalProps = getEmbedVideoModalProps(editor, target);

  if (isDesktopEmailEditorViewport()) {
    openEmailStockMediaPopup({
      overrides: buildStockVideosPickerProps(editor, target),
      initialView: 'video-options',
      onNavigateBack: getOpenBlockPanelBackTarget(),
      arrowAnchorTileId: getArrowAnchorTileIdForOpenBlockPanel(),
      videoOptions: {
        onMyUploads: (): void => {
          openMyUploadsVideo(editor, target);
        },
        onEmbedVideo: (): void => {
          openEmbedVideosEmailmakerModal(videoModalProps);
        },
      },
    });
    return;
  }

  openModalAfterTouchSettles((): void => {
    openMediaTypeModal({
      title: window.i18next.t('pmwjs_select_an_option'),
      videoQualityFiltersDisabled: true,
      mediaType: MediaType.VIDEO,
      onAddSelected: (videos: VideoData[]): void => {
        handleStockMediaVideoSelection(editor, videos, target);
      },
      hideRecordingOptions: true,
      maxSelectionNumber: 1,
      uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
      showEmbedVideo: true,
      onAddEmbeddedVideo: videoModalProps.onAddVideo,
      onVideoUploadRequest: videoModalProps.onVideoUploadRequest,
      onVideoUploadError: videoModalProps.onVideoUploadError,
      onBack: (): void => {
        openEmbedVideo(editor);
      },
    });
  });
}

/** My-uploads video picker for the component (or selection). */
export function openMyUploadsVideo(editor: Editor, targetComponent?: Component): void {
  const component = targetComponent ?? editor.getSelected();
  if (!component) {
    return;
  }

  openUserMediaModal({
    hideRecordingOptions: true,
    uploadAcceptedMimetype: 'video/*,image/gif',
    onStartAddSelected: createImageLoadingStarter(editor, component),
    onAddSelected: (videos: VideoData[]): void => {
      handleStockMediaVideoSelection(editor, videos, component);
    },
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.VIDEO,
    mediaTypesToShow: [UploadsMediaType.VIDEO],
    uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
    videoQualityFiltersDisabled: true,
  });
}

/** Stock videos picker for the current selection. */
export function openExploreMediaVideo(editor: Editor): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  openStockMediaPicker(buildStockVideosPickerProps(editor, component));
}

/** AI video generator for the component (or selection). Desktop opens the generator directly in the popup. */
export function openAiVideos(editor: Editor, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  openStockMediaPicker(buildStockVideosPickerProps(editor, target), 'ai-videos');
}

/** The Videos stock panel props, shared by the direct Explore-media flow and the Video chooser popup. */
function buildStockVideosPickerProps(editor: Editor, component: Component): StockModalProps {
  return buildStockVideosPickerPropsForTarget(editor, (): Component | undefined => component);
}

/** Target-provider variant: the fresh-add chooser resolves (and lazily creates) the placeholder only when a pick happens. */
function buildStockVideosPickerPropsForTarget(editor: Editor, getTarget: () => Component | undefined): StockModalProps {
  return {
    onStartAddSelected: (): void => {
      const target = getTarget();
      if (target) {
        createImageLoadingStarter(editor, target)();
      }
    },
    onAddSelected: (videos: VideoData[]): void => {
      const target = getTarget();
      if (target) {
        handleStockMediaVideoSelection(editor, videos, target);
      }
    },
    tabsToShow: [StockPanelTabs.VIDEOS],
    defaultSearchTab: StockPanelTabs.VIDEOS,
    maxSelectionNumber: 1,
    populateRecentlyAddedOnItemAdd: true,
    videoQualityFiltersDisabled: true,
    panelId: VIDEO_STOCK_PANEL_ID,
    desktopHeaderTitle: window.i18next.t('pmwjs_videos_2'),
  };
}

/** Embed-video URL modal for the current selection. */
export function openEmbedVideo(editor: Editor): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  openEmbedVideosEmailmakerModal(getEmbedVideoModalProps(editor, component));
}

/** Applies the picked image (converting a design or icon component back to an image first). */
function handleStockMediaImageSelection(editor: Editor, images: ImageData[], component: Component): void {
  if (images.length === 0) {
    return;
  }

  const imageComponent = convertComponentToImageIfNeeded(editor, component);

  if (images[0].source === UserImageSource.EMAIL_UPLOAD || images[0].source === UserImageSource.UPLOAD) {
    trackGaEventForComponent(editor, GA4EventName.EMAILMAKER_UPLOAD_MEDIA, imageComponent);
  }

  void setImageSrc(editor, {
    component: imageComponent,
    source: images[0].source,
    hashedFilename: images[0].hashedFilename,
  });
}

/**
 * Replacing media with an image turns a design or icon component into a plain image component,
 * so its serialized media type (which backend preset creation is keyed off) matches the new content.
 */
function convertComponentToImageIfNeeded(editor: Editor, component: Component): Component {
  const convertedComponent = convertComponentIfNeeded(editor, component, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN, EMAIL_MAKER_BLOCKS.ID_EXPLORE_MEDIA);
  return convertComponentIfNeeded(editor, convertedComponent, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON, EMAIL_MAKER_BLOCKS.ID_EXPLORE_MEDIA);
}

/** Applies the picked sticker, recording still vs animated in the media-type prop. */
function handleStockMediaStickerSelection(editor: Editor, stickers: StickerData[], component: Component): void {
  if (stickers.length === 0) {
    return;
  }

  component.set(EMAIL_MAKER_PROPS.MEDIA_TYPE, stickers[0].type === ElementDataType.ANIMATED_STICKER ? EmailMakerMediaType.ANIMATED_STICKER : EmailMakerMediaType.STILL_STICKER);

  void setImageSrc(editor, {
    component,
    source: getStickerImageSource(stickers),
    hashedFilename: stickers[0].hashedFilename,
  });
}

/** Animated stickers have no image-item source constant; the backend keys their presets on filename + media type. */
function getStickerImageSource(stickers: StickerData[]): ImageItemSourceType | undefined {
  if (stickers[0].type === ElementDataType.STILL_STICKER) {
    return stickers[0].source;
  }

  if (stickers[0].type === ElementDataType.EXTRACTED_GETTY_STICKER) {
    return ImageItemSource.PMW_EXTRACTED_GETTY_STICKER;
  }

  return undefined;
}

/** Applies the picked icon, ignoring non-icon selections. */
function handleStockMediaIconSelection(editor: Editor, icons: ElementData[], component: Component): void {
  if (icons.length === 0) {
    return;
  }

  const icon = icons[0];
  if (icon.type !== ElementDataType.ICON && icon.type !== ElementDataType.AI_ICON) {
    return;
  }

  component.set(EMAIL_MAKER_PROPS.MEDIA_TYPE, EmailMakerMediaType.ICON);

  void setImageSrc(editor, {
    component,
    source: icon.type === ElementDataType.AI_ICON ? ImageItemSource.PMW_AI_ICON : ImageItemSource.PMW_ICON,
    hashedFilename: icon.hashedFilename,
  });
}

/** Applies the picked video, clearing any embed-video link state. */
function handleStockMediaVideoSelection(editor: Editor, videos: VideoData[], component: Component): void {
  if (videos.length === 0) {
    return;
  }

  component.removeAttributes([DATA_URL_ATTRIBUTE]);
  component.addAttributes({[EMBEDDED_VIDEO_ATTRIBUTE]: 0});

  if (videos[0].source === UserVideoSource.EMAIL_UPLOAD || videos[0].source === UserVideoSource.UPLOAD) {
    trackGaEventForComponent(editor, GA4EventName.EMAILMAKER_UPLOAD_MEDIA, component);
  }

  removeComponentAnchor(editor, component);

  void setImageSrc(editor, {
    component,
    source: videos[0].source,
    hashedFilename: videos[0].hashedFilename,
  });
}

/** Shared embed-video modal callbacks for a component. */
function getEmbedVideoModalProps(editor: Editor, component: Component): EmbedVideosEmailmakerInputModalProps {
  return {
    onAddVideo: (videoUrl: string, thumbnailUrl: string): void => {
      setEmbeddedVideoSrc(editor, {component, thumbnailURL: thumbnailUrl, videoURL: videoUrl});
    },
    onVideoUploadRequest: createImageLoadingStarter(editor, component),
    onVideoUploadError: (errorMessage: string): void => {
      openErrorModal({message: errorMessage});
      setImageLoading(editor, {component, loading: false});
    },
    onBack: noop,
  };
}

function createImageLoadingStarter(editor: Editor, component: Component): () => void {
  return (): void => {
    setImageLoading(editor, {component, loading: true, text: window.i18next.t('pmwjs_loading')});
  };
}
